import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldCheck, Copy, ArrowRight, Download, KeyRound, LogOut } from "lucide-react";

/**
 * TOTP setup — user scans a QR (or pastes the secret manually into 1Password /
 * Authy / Google Authenticator) then confirms a 6-digit code to activate.
 * Super-admins can't leave this page until MFA is enabled.
 */
export default function MfaSetup() {
  const { user, refresh, logout } = useAuth();
  const [step, setStep] = useState("scan");   // "scan" | "verify" | "codes" | "done"
  const [setup, setSetup] = useState(null);   // {secret, qr_data_url, otpauth_uri}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);   // shown ONCE after activation
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/auth/mfa/setup");
        setSetup(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to start MFA setup");
      }
    })();
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { code });
      toast.success("Two-factor authentication activated");
      setRecoveryCodes(data.recovery_codes || []);
      setStep("codes");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(setup?.secret || "");
    toast.success("Secret copied");
  };

  const copyCodes = () => {
    navigator.clipboard.writeText((recoveryCodes || []).join("\n"));
    toast.success("Recovery codes copied");
  };

  const downloadCodes = () => {
    const body = `ForensiQ — MFA Recovery Codes
Account: ${user?.email}
Generated: ${new Date().toISOString()}

Each code can be used ONCE. Store them somewhere safe (password manager,
printed and locked away). If you lose your authenticator, sign in with one
of these codes and rotate them from the profile menu.

${(recoveryCodes || []).map((c, i) => `${(i+1).toString().padStart(2,'0')}. ${c}`).join("\n")}
`;
    const blob = new Blob([body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ForensiQ-MFA-Recovery-${(user?.email || "user").replace(/[^a-z0-9]/gi, "_")}.txt`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const finish = () => {
    setStep("done");
    setTimeout(() => nav("/"), 800);
  };

  // Escape hatch — user typed wrong email / wants to log in as someone else.
  const signOut = () => {
    logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#060606] text-white flex items-center justify-center p-6" data-testid="mfa-setup-page">
      {/* Escape hatch — helps users who logged in with the wrong account. */}
      <button onClick={signOut} data-testid="mfa-signout"
        className="fixed top-4 right-4 z-[100] inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-white/15 bg-[#060606]/95 text-[#8E9196] hover:text-white hover:border-white/40 rounded-sm">
        <LogOut className="w-3.5 h-3.5" /> Sign out &amp; use another account
      </button>
      <div className="max-w-lg w-full border border-white/15 bg-[#0a0a0a] rounded-sm p-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[#B57BFF]">
            <ShieldCheck className="w-5 h-5" />
            <div className="text-[10px] font-mono uppercase tracking-widest">Two-factor · required for super-admins</div>
          </div>
          {user?.email && (
            <div className="text-[10px] font-mono text-[#8E9196]" data-testid="mfa-current-user">
              {user.email}
            </div>
          )}
        </div>
        <h1 className="font-display font-black text-3xl mb-2">Secure your account</h1>
        <p className="text-[#8E9196] text-sm mb-6">
          Scan the QR code with any TOTP app — Google Authenticator, 1Password,
          Authy, Microsoft Authenticator — then enter the 6-digit code to activate.
        </p>

        {step === "scan" && setup && (
          <>
            <div className="flex items-start gap-6 mb-6">
              <img src={setup.qr_data_url} alt="TOTP QR" data-testid="mfa-qr"
                   className="w-40 h-40 bg-white p-2 rounded-sm shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Or enter this secret manually</div>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-sm text-[#00F0FF] break-all">{setup.secret}</code>
                  <button onClick={copySecret} data-testid="mfa-copy" className="p-1 text-[#8E9196] hover:text-white">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="text-[10px] font-mono text-[#52525B] mt-2 break-all">
                  Account: {user?.email}<br />Issuer: ForensiQ
                </div>
              </div>
            </div>
            <button onClick={() => setStep("verify")} data-testid="mfa-continue"
              className="w-full flex items-center justify-center gap-2 border border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest">
              I&apos;ve scanned it — continue <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}

        {step === "verify" && (
          <form onSubmit={verify}>
            <label className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Enter 6-digit code</label>
            <input
              autoFocus inputMode="numeric" autoComplete="one-time-code"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))}
              maxLength={8} data-testid="mfa-verify-code" required
              className="w-full mt-1 mb-4 bg-transparent border border-white/15 rounded-sm px-3 py-2.5 font-mono text-2xl tracking-widest text-center outline-none focus:border-[#B57BFF]"
              placeholder="000000"
            />
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep("scan")}
                className="border border-white/15 text-[#8E9196] hover:text-white px-3 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest">Back</button>
              <button type="submit" disabled={busy} data-testid="mfa-verify-submit"
                className="flex-1 border border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50">
                {busy ? "Verifying…" : "Activate MFA"}
              </button>
            </div>
          </form>
        )}

        {step === "done" && (
          <div className="text-center py-4">
            <ShieldCheck className="w-12 h-12 text-[#34C759] mx-auto mb-3" />
            <div className="font-display text-xl">MFA activated</div>
            <div className="text-[#8E9196] text-sm mt-1">Redirecting…</div>
          </div>
        )}

        {step === "codes" && recoveryCodes && (
          <div data-testid="mfa-recovery-codes">
            <div className="flex items-center gap-2 text-[#B57BFF] mb-2">
              <KeyRound className="w-4 h-4" />
              <div className="text-[10px] font-mono uppercase tracking-widest">One-time recovery codes</div>
            </div>
            <div className="font-display font-black text-2xl mb-2">Save these codes now</div>
            <p className="text-[#8E9196] text-sm mb-4">
              Each code lets you sign in <b>once</b> if you lose your authenticator device.
              We&apos;ll never show them again — save them in a password manager or print and lock them away.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-4 border border-[#B57BFF]/30 bg-[#B57BFF]/[0.05] rounded-sm p-3 font-mono text-sm tracking-widest">
              {recoveryCodes.map((c, i) => (
                <div key={i} className="text-white select-all" data-testid={`recovery-code-${i}`}>
                  <span className="text-[#52525B] mr-2">{String(i + 1).padStart(2, "0")}.</span>{c}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={copyCodes} data-testid="copy-codes"
                className="flex items-center gap-1.5 border border-white/15 hover:border-[#00F0FF] hover:text-[#00F0FF] text-[#8E9196] px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
              <button onClick={downloadCodes} data-testid="download-codes"
                className="flex items-center gap-1.5 border border-white/15 hover:border-[#00F0FF] hover:text-[#00F0FF] text-[#8E9196] px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
                <Download className="w-3.5 h-3.5" /> Download .txt
              </button>
              <button onClick={finish} data-testid="mfa-codes-continue"
                className="ml-auto flex items-center gap-1.5 border border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
                I&apos;ve saved them <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
